// statsUtils.ts
// Utilidades para filtrar y agregar estadísticas de citas (bookings) con ChartSpec local.

import moment from 'moment'
import { Booking } from 'src/types/pages.types'

/**
 * Especificación para generar gráficos dinámicamente.
 */
export interface ChartSpec {
  chartType: 'pie' | 'line' | 'bar'
  metric: 'Sesiones' | 'Tiempo'
  /**
   * Dimensión de los datos:
   * - 'month' | 'day' para series temporales
   * - 'affectedZone' | 'completed' | 'process' | 'gender' para pie charts
   */
  dimension: 'month' | 'day' | 'affectedZone' | 'completed' | 'process' | 'gender' | 'store' | 'sector' | 'role'
  filters: {
    gender?: string
    ageMin?: number
    ageMax?: number
    storeId?: string
    companyId?: string
    completed?: boolean
    processId?: string
    [key: string]: any
  }
  period?: { from: string; to: string }
  description?: string // Descripción amigable del gráfico
}

/**
 * Filtra un array de bookings según criterios.
 */
export function filterBookings(bookings: Booking[], filters: ChartSpec['filters']): Booking[] {
  return bookings.filter((b) =>
    Object.entries(filters).every(([key, val]) => {
      if (val == null) return true
      switch (key) {
        case 'gender':
          return b.patient.all.gender.id === (val as string)
        case 'ageMin':
          return moment().diff(moment(b.patient.all.birthday), 'years') >= Number(val)
        case 'ageMax':
          return moment().diff(moment(b.patient.all.birthday), 'years') <= Number(val)
        case 'storeId':
          return b.storeId === (val as string)
        case 'companyId':
          return b.companyId === (val as string)
        case 'completed':
          return b.completed === Boolean(val)
        case 'processId':
          return b.campaign?.all.process.id === val || b.incident?.all.process.id === val
        default:
          return true
      }
    })
  )
}

/**
 * Agrega series de Sesiones y Tiempo por mes o día.
 */
export function aggregateTimeSeries(bookings: Booking[], spec: ChartSpec): Array<{ [key: string]: number }> {
  const { period, dimension } = spec
  let buckets: Array<any> = []

  if (dimension === 'month') {
    buckets = Array.from({ length: 12 }, (_, i) => ({ month: i + 1, Sesiones: 0, Tiempo: 0 }))
  } else if (dimension === 'day') {
    buckets = Array.from({ length: 31 }, (_, i) => ({ day: i + 1, Sesiones: 0, Tiempo: 0 }))
  } else {
    return []
  }

  bookings
    .filter((b) => {
      if (!period) return true
      const d = moment(b.date, 'YYYY-MM-DD')
      return d.isBetween(moment(period.from), moment(period.to), 'day', '[]')
    })
    .forEach((b) => {
      const idx = dimension === 'month' ? moment(b.date, 'YYYY-MM-DD').month() : moment(b.date, 'YYYY-MM-DD').date() - 1

      buckets[idx].Sesiones += 1
      buckets[idx].Tiempo += Number(b.minutes)
    })

  return buckets
}

/**
 * Agrega datos para pie chart según dimensión.
 */
export function aggregatePie(bookings: Booking[], spec: ChartSpec): Array<{ name: string; value: number }> {
  const counter = new Map<string, number>()

  bookings.forEach((b) => {
    let key = 'Otros'
    switch (spec.dimension) {
      case 'affectedZone':
        key = b.affectedZone.name
        break
      case 'completed':
        key = b.completed ? 'Asiste' : 'No asiste'
        break
      case 'process':
        key = b.campaign?.all.process.name || b.incident?.all.process.name || 'Desconocido'
        break
      case 'gender':
        key = b.patient.all.gender.name
        break
      default:
        key = 'Otros'
    }
    counter.set(key, (counter.get(key) || 0) + 1)
  })

  return Array.from(counter.entries()).map(([name, value]) => ({ name, value }))
}

export function aggregateByCategory(bookings: Booking[], spec: ChartSpec): Array<{ name: string; value: number }> {
  const counter = new Map<string, number>()

  bookings.forEach((b) => {
    let key = 'Otros'
    switch (spec.dimension) {
      case 'store':
        key = b.store?.name || 'Desconocido'
        break
      case 'affectedZone':
        key = b.affectedZone?.name || 'Desconocido'
        break
      case 'completed':
        key = b.completed ? 'Asiste' : 'No asiste'
        break
      case 'process':
        key = b.campaign?.all.process.name || b.incident?.all.process.name || 'Desconocido'
        break
      case 'gender':
        key = b.patient.all.gender.name
        break
      // …
    }
    counter.set(key, (counter.get(key) || 0) + 1)
  })

  return Array.from(counter.entries()).map(([name, value]) => ({ name, value }))
}
